import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import { AGENT_URL } from '../config/env'
import type { FeatureFlags as OrgFeatureFlags } from '@/types/configuration'

export interface SuggestedPrompt {
  title: string
  message: string
}

export interface AgentConfig {
  enabled: boolean
  suggested_prompts: SuggestedPrompt[]
}

export type FeatureFlags = Partial<OrgFeatureFlags>

export interface Configuration {
  agents: {
    [agentName: string]: AgentConfig
  }
  features?: FeatureFlags
}

async function fetchConfiguration(token: string): Promise<Configuration> {
  const response = await fetch(`${AGENT_URL}/configuration`, {
    headers: {
      Authorization: token,
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch configuration: ${response.status}`)
  }

  return response.json()
}

/**
 * Hook to fetch and cache organization configuration using TanStack Query.
 *
 * Configuration is automatically refetched when the organization changes.
 * Results are cached for 5 minutes to minimize API calls.
 *
 * @returns {Object} Configuration state with same API as before
 * @returns {Configuration | null | undefined} configuration - The org config, null on error, undefined while loading
 * @returns {boolean} loading - True while fetching
 * @returns {Error | null} error - Error object if fetch failed
 * @returns {boolean} isPreviousOrgData - True while showing the prior org's config
 */
export function useConfiguration() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  const query = useQuery({
    queryKey: ['org-config', organization?.id],
    queryFn: async () => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return fetchConfiguration(token)
    },
    enabled: !!organization?.id,
    placeholderData: (previousData) => previousData,
  })

  // Map TanStack Query state to our existing API
  return {
    configuration: query.error ? null : (query.data ?? null),
    loading: query.isLoading || query.isPending,
    error: query.error instanceof Error ? query.error : null,
    /**
     * True while `placeholderData` is serving the PREVIOUS organization's config
     * because the new org's fetch is still in flight. The query key is
     * org-scoped, so `configuration` is real data — just for the wrong org.
     * Anything gating access on an org's flags has to wait for this to clear.
     */
    isPreviousOrgData: query.isPlaceholderData,
  }
}
